/**
 * Tier-entitlement gate primitive (M7). The ONE shape every capability gate
 * (M8 analysis tools, M9 demographics overlays, …) is built from, so "is this
 * account allowed?" is decided in a single place off a single input.
 *
 * HARD INVARIANT: the entitlement is TIER-derived (`isPaid`) — who the account
 * IS (its storage-keyed tier), never how much it has consumed. The authoritative
 * `Entitlement` ({tier,isPaid,subscriptionStatus}) structurally satisfies
 * `TierEntitlement`, so a server-resolved entitlement flows straight in.
 *
 * Client-safe: pure + synchronous, no pg / server-only imports — the feature
 * gate modules that build on this stay importable from a client bundle.
 */

/** The minimal entitlement a capability gate reads (tier-derived, never usage). */
export interface TierEntitlement {
  /** True when the account is on a paid tier (tier-derived, never usage-derived). */
  isPaid: boolean;
}

/** Why a capability can't open: absent from the build, or present-but-unentitled. */
export type CapabilityBlock = "build_off" | "not_entitled" | null;

export interface CapabilityGate {
  /** Build-go: is the capability present in the UI/build at all? */
  present: boolean;
  /** present AND entitled — the capability may open. */
  openable: boolean;
  /** Why it can't open (or null when it can). */
  blockedReason: CapabilityBlock;
  /** present but NOT entitled — show the Free-tier locked/upsell seam. */
  locked: boolean;
}

/**
 * The shared gate decision. `present` is the caller's resolved build-go / avail-
 * ability verdict (a feature can layer extra co-requisites — e.g. a bound render
 * engine — BEFORE calling this). When present, entitlement decides openable vs
 * the locked seam. When absent, entitlement is never consulted.
 */
export function capabilityGate(present: boolean, entitlement: TierEntitlement): CapabilityGate {
  if (!present) return { present: false, openable: false, blockedReason: "build_off", locked: false };
  const entitled = entitlement.isPaid;
  return { present: true, openable: entitled, blockedReason: entitled ? null : "not_entitled", locked: !entitled };
}
